/**
 * Whether an offer can still be built out of the items the menu is actually
 * serving.
 *
 * The old rule asked only whether *every* target was gone, which is too weak
 * for an offer that needs several: "buy 3 of these 4" is impossible once two
 * are out of stock, and the offer used to keep advertising itself until
 * calculate rejected it. The rule here is the one the offer states — can the
 * required count be reached — applied to each side of it.
 *
 * Kept free of `LoyaltyItemOffer` and `offerSelection` on purpose: both of
 * those already reference each other, and the offer class needs this.
 */
import { LoyaltyTargetConfig, LoyaltyTargetType } from "../loyaltyTypes";

/** How many units one entity may contribute — mirrors `buildEntity`. */
const capacityOf = (entity: {
  minimumQuantity?: number;
  maximumQuantity?: number;
}): number => {
  const min = Math.max(entity.minimumQuantity ?? 0, 0);

  return Math.max(entity.maximumQuantity ?? 1, min, 1);
};

/**
 * Whether this side of the offer can still reach its required count.
 *
 * A target the guest has no say over — one carrying a `minimumQuantity` — makes
 * the whole side impossible the moment it is unavailable, however much the
 * others could cover. Otherwise it is a matter of capacity: every available
 * target contributes up to its `maximumQuantity`.
 *
 * A config that is not an ITEMS target, or has no targets at all, imposes no
 * requirement and so is always satisfiable.
 */
export function canFillFromAvailable(
  config: LoyaltyTargetConfig | undefined,
  isAvailable: (entityId: string) => boolean
): boolean {
  if (config?.targetType !== LoyaltyTargetType.ITEMS) {
    return true;
  }

  const entities = config.targetEntities ?? [];
  if (!entities.length) {
    return true;
  }

  const compulsoryIsGone = entities.some(
    (entity) =>
      Math.max(entity.minimumQuantity ?? 0, 0) > 0 &&
      !isAvailable(entity.entityId)
  );

  if (compulsoryIsGone) {
    return false;
  }

  const capacity = entities
    .filter((entity) => isAvailable(entity.entityId))
    .reduce((total, entity) => total + capacityOf(entity), 0);

  return capacity >= Math.max(config.minimumItemCount ?? 0, 0);
}
